import {
  ConnectionState,
  eStreamConnectionStateAtom,
  isDefined,
  ListedInstrument,
  useInstrumentStoreItem,
} from '@eih-netbroker/common';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { ReactComponent as ErrorOutlineIcon } from '../../../../../assets/icons/alert_line.svg';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { useTheme } from '../../../../../context/NbThemeContext';
import { NbStatTagInline } from '../../../../../shared/components/change-direction/NbStatTagInline';
import { InstumentMarketPriceDisplay } from '../../../../../shared/components/instrument-market-price-display/InstrumentMarketPriceDisplay';
import { NbIndefiniteLoader } from '../../../../../shared/components/loader/Loader';
import { NbTooltip } from '../../../../../shared/components/tooltip/Tooltip';
import { instrumentStore } from '../../../../../shared/data/instrumentStore';
import { useEStream } from '../../../../../shared/services/sse/useEStream';
import classes from './PriceInfo.module.scss';

const CONNECTING_LOADER_DELAY = 300;

type PriceInfoProps = {
  instrument: ListedInstrument | null;
  source: string;
  dataTestId?: string;
};

export const PriceInfo = ({ instrument, source, dataTestId }: PriceInfoProps) => {
  const { translation } = useLocale();
  const { subscribeToInstrumentChanges } = useEStream();
  const eStreamConnectionState = useAtomValue(eStreamConnectionStateAtom);
  const [showConnectingLoader, setShowConnectingLoader] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    if (eStreamConnectionState === ConnectionState.Connecting) {
      const timer = setTimeout(() => setShowConnectingLoader(true), CONNECTING_LOADER_DELAY);
      return () => clearTimeout(timer);
    }
    setShowConnectingLoader(false);
  }, [eStreamConnectionState]);

  const instrumentStoreItem = useInstrumentStoreItem(instrument, instrumentStore, subscribeToInstrumentChanges, {
    source,
  });

  const priceChange = useMemo(() => {
    if (isDefined(instrument) && instrumentStoreItem?.price?.value && instrumentStoreItem?.closePrice) {
      return instrumentStoreItem.price.value - instrumentStoreItem.closePrice;
    }
    return undefined;
  }, [instrument, instrumentStoreItem]);

  const priceChangePerc = useMemo(() => {
    if (
      isDefined(instrument) &&
      instrumentStoreItem?.price?.value &&
      instrumentStoreItem?.closePrice &&
      isDefined(priceChange)
    ) {
      return priceChange / instrumentStoreItem.closePrice;
    }
    return undefined;
  }, [instrument, instrumentStoreItem, priceChange]);

  if (!isDefined(instrument)) {
    return null;
  }

  if (
    eStreamConnectionState === ConnectionState.Disconnected ||
    eStreamConnectionState === ConnectionState.FailedToConnected ||
    (eStreamConnectionState === ConnectionState.Connecting && !showConnectingLoader)
  ) {
    return (
      <NbTooltip
        title={translation.app.estream.unavailable}
        placement={'bottom'}
        data-testid={`${dataTestId}-unavailable`}
      >
        <span className={classes.errorIcon}>
          <ErrorOutlineIcon />
        </span>
      </NbTooltip>
    );
  }

  if (showConnectingLoader || !isDefined(priceChange) || !isDefined(priceChangePerc)) {
    return (
      <NbIndefiniteLoader
        isContrast={!isDark}
        isTransparent={isDark}
        size={200}
        className={classes.loader}
        data-testid={`${dataTestId}-loading`}
      />
    );
  }

  if (eStreamConnectionState === ConnectionState.Connected) {
    return (
      <div
        className={classes.priceSection}
        data-testid={dataTestId}
      >
        <InstumentMarketPriceDisplay
          instrument={instrument}
          withCurrency
          price={instrumentStoreItem?.price}
          className={classes.headerPriceText}
        />
        <NbStatTagInline
          direction={isDefined(priceChange) ? (priceChange < 0 ? 'down' : 'up') : 'none'}
          changeValue={priceChange}
          changePercentage={priceChangePerc}
        />
      </div>
    );
  }
};
